import { FileDropzone } from "components/FileUpload/FileDropzone";
import SectionPanel from "components/panels/SectionPanel/SectionPanel";
import debounce from "lodash/debounce";
import React from "react";
import { Message, Progress } from "semantic-ui-react";
import { uploadFilesToCoreApi } from "utilities/fileUploadUtils";
import { Modal } from "../../components";
import DeleteDialog from "../../components/deleteDialog";
import FakeProgress from "../../components/FakeProgress";
import { NoDataAvailableDiv } from "../../components/NoDataAvailableDiv";
import PanelSegmentType from "../../components/panels/PanelSegment/PanelSegment";
import FilesList from "../components/File/FilesList";
import FromStorage from "../components/modals/FromStorage";
import FromTemplate from "../components/modals/FromTemplate";
import { getAcceptForFileType } from "shared/utils/fileTypeUtils";
import { getDocumentCoreApi } from "utilities/apiUtils";

interface Props {
  label: string
  fileGroup: server.dto.ESignFileGroups
  files: server.dto.ESignFile[]
  onFilesUpdated: (files: server.dto.ESignFile[]) => void
  error: boolean
  documentDetails: server.dto.ESignDocument
  collapsible: boolean
  startUncollapsed: boolean
  articleId?: number
  bookkeeping: server.dto.BookkeepingSmall
  divId?: string
  validateFromTemplateClick?: () => boolean
}

const FilesPanel = (props: Props) => {
  const [uploadError, setUploadError] = React.useState<string | undefined>(undefined);
  const [deletingFile, setDeletingFile] = React.useState<server.dto.ESignFile | undefined>(undefined);
  const [isDraggingFile, setIsDraggingFile] = React.useState(false);
  const [isFromStorageOpen, setIsFromStorageOpen] = React.useState(false);
  const [isFromTemplateOpen, setIsFromTemplateOpen] = React.useState(false);
  const [uploadPercentage, setUploadPercentage] = React.useState(undefined);
  const [isFakeUploadingDone, setIsFakeUploadingDone] = React.useState(undefined);
  const filesRef = React.useRef<HTMLInputElement>(undefined);
  const debouncedStopDragging = React.useCallback(debounce(() => { setIsDraggingFile(false); }, 200), [setIsDraggingFile]);

  const onNewUploadClick = (e) => {
    e.stopPropagation();
    setUploadError(undefined);
    filesRef.current.click();
  };

  const refreshFiles = async () => {
    const newFiles = await window.CoreApi.ESign.ListESignFiles(props.documentDetails.documentId, props.fileGroup);

    props.onFilesUpdated(newFiles.data.data);
  };

  const upload = async (files: FileList | File[]) => {
    setIsDraggingFile(false);

    setUploadPercentage(0);
    const res = await uploadFilesToCoreApi(
      window.CoreApi.ESign.CreateESignFilesFromUpload,
      window.CoreApi.ESign.CreateESignFilesFromUpload.absoluteUrl(),
      {
        documentId: props.documentDetails.documentId,
        fileGroup: props.fileGroup,
        files: Array.from(files)
      },
      {
        overrideMaxFileLength: props.fileGroup === "needToBeSigned"
          ? serverReference.constants.eSignFile.maxFileThatNeedsToBeSignedContentLength
          : serverReference.constants.eSignFile.maxOtherFileContentLength,
        fileType: props.fileGroup === "needToBeSigned" ? "pdf" : "any",

        onUploadProgress: (e) => {
          const percentCompleted = Math.round((e.loaded * 100) / e.total);
          setUploadPercentage(percentCompleted);
        }
      }
    );
    if (res.status !== 200 || res.data.hasErrors) {
      setUploadError(res.data.errors[0]?.message || RESX.ExceptionMessage.ThereWasAnError);
      return false;
    }
    setUploadError(undefined);

    return true;
  };

  const onFilesChange = async () => {
    if (filesRef.current.files.length === 0) {
      return;
    }

    const isSuccessful = await upload(filesRef.current.files);
    if (isSuccessful) {
      await refreshFiles();
    }

    setUploadPercentage(undefined);
    filesRef.current.value = "";
  };

  const onFileDrop = async (files: File[]) => {
    const isSuccessful = await upload(files);
    if (isSuccessful) {
      await refreshFiles();
    }
    setUploadPercentage(undefined);
  };

  const onOrderChanged = async (files: server.dto.ESignFile[]) => {
    props.onFilesUpdated(files);
    return window.CoreApi.ESign.UpdateESignFileOrder({
      documentId: props.documentDetails.documentId,
      fileGroup: props.fileGroup,
      eSignFileIds: files.map(f => f.eSignFileId)
    });
  };

  const onEditSaved = async (newFileName: string, eSignFileId: number) => {
    const updateRes = await window.CoreApi.ESign.UpdateESignFileName({
      eSignFileId,
      name: newFileName
    });

    props.onFilesUpdated(updateRes.data.data);
  };

  const onDeleted = async () => {
    await window.CoreApi.ESign.DeleteESignFile(deletingFile.eSignFileId);
    setDeletingFile(undefined);
    return refreshFiles();
  };

  const onFilesFromStorageSelected = async (files: server.dto.File[]) => {
    if (files.length === 0) {
      setIsFromStorageOpen(false);
      return;
    }

    setIsFakeUploadingDone(false);
    setIsFromStorageOpen(false);
    setUploadError(undefined);

    try {
      await window.CoreApi.ESign.CreateESignFilesFromStorage({
        documentId: props.documentDetails.documentId,
        fileGroup: props.fileGroup,
        clientFileIds: files.map(f => f.id)
      });

      await refreshFiles();
      setIsFakeUploadingDone(true);
      setUploadError(undefined);
    } catch {
      setIsFakeUploadingDone(undefined);
      setUploadError(RESX.ExceptionMessage.ThereWasAnError);
    }
  };

  const onTemplateSelected = async (template: server.dto.WordTemplate) => {
    setIsFromTemplateOpen(false);
    setIsFakeUploadingDone(false);
    setUploadError(undefined);
    try {
      await window.CoreApi.ESign.CreateESignFileFromTemplate({
        documentId: props.documentDetails.documentId,
        fileGroup: "needToBeSigned",
        wordTemplateForDetailViewId: template.wordTemplateId
      });
      await refreshFiles();
      setIsFakeUploadingDone(true);
    } catch {
      setIsFakeUploadingDone(undefined);
      setUploadError(RESX.ExceptionMessage.ThereWasAnError);
    }
  };

  React.useEffect(() => {
    const dragStart = () => {
      setIsDraggingFile(true);
      debouncedStopDragging();
    };
    const dragEnd = debouncedStopDragging;
    document.body.addEventListener("dragenter", dragStart);
    document.body.addEventListener("dragover", dragEnd);
    return () => {
      document.body.removeEventListener("dragenter", dragStart);
      document.body.removeEventListener("dragover", dragEnd);
    };
  }, []);

  const acceptedFiles = props.fileGroup === "needToBeSigned" ? ".pdf" : undefined;

  const onFakeUploadFinished = () => {
    setIsFakeUploadingDone(undefined);
  };

  const fetchRelatedFilesApi = async () => getDocumentCoreApi(props.documentDetails.documentType).ListRelatedFiles(props.documentDetails.documentId);

  const onFromTemplateClick = () => {
    if (props.validateFromTemplateClick) {
      const result = props.validateFromTemplateClick();
      if (!result) return;
    }
    setIsFromTemplateOpen(true);
  }

  let display: "dropzone" | "uploadprogress" | "filelist" | "nodatafound" | "fakeupload";

  if (uploadPercentage != null) display = "uploadprogress";
  else if (isFakeUploadingDone != null) display = "fakeupload";
  else if (isDraggingFile) display = "dropzone";
  else if (props.files.length > 0) display = "filelist";
  else display = "nodatafound";

  return <SectionPanel.Panel
    collapsible={!props.collapsible ? undefined : {
      start: props.startUncollapsed ? "open" : "closed",
      persist: false,
      uniqueRowId: "filesPanelForEsign"
    }}
    header={<>
      <PanelSegmentType.Title title={props.label} />
      <PanelSegmentType.SupportQuestionMark articleId={props.articleId} />
      <div className="flex gap-small">
        {props.fileGroup === "needToBeSigned" &&
          <PanelSegmentType.Button onClick={onFromTemplateClick} label={RESX.Template.resxTemplate} type="secondary" />}
        <PanelSegmentType.Button
          onClick={(e) => {
            e.stopPropagation();
            setIsFromStorageOpen(true);
          }}
          label={RESX.File.resxFile}
          type="secondary"
        />
        <PanelSegmentType.Button onClick={onNewUploadClick} label={RESX.Buttons.Upload} type="secondary" />
      </div>
    </>}

    id={props.divId}
  >
    {uploadError && <Message color="red" className="margin-bottom margin-top">
      <div dangerouslySetInnerHTML={{ __html: uploadError }}></div>
    </Message>}
    <input
      type="file"
      multiple
      style={{ display: "none" }}
      ref={filesRef}
      accept={acceptedFiles}
      onChange={onFilesChange}
    />
    {
      display === "filelist" && <FilesList
        files={props.files}
        onFileDelete={setDeletingFile}
        onFileOrderChange={onOrderChanged}
        onEditSaved={onEditSaved}
      />
    }

    {display === "nodatafound" && <NoDataAvailableDiv error={props.error} />}

    {
      display === "dropzone" && <FileDropzone
        onDrop={onFileDrop}
        onDropReject={(fileRejections) => {
          setUploadError(fileRejections[0].errors[0].message);
        }}
        maxSizeBytes={serverReference.constants.file.maximumClientFileContentLength}
        accept={getAcceptForFileType(props.fileGroup === "needToBeSigned" ? "pdf" : "any")}
        iconType={props.fileGroup === "needToBeSigned" ? "pdf" : "file"}
      />
    }

    {
      display === "uploadprogress" && <div>
        <Progress percent={uploadPercentage} color="green" inverted progress active />
      </div>
    }
    {
      display === "fakeupload" && <div>
        <FakeProgress isDone={isFakeUploadingDone} onFinish={onFakeUploadFinished} />
      </div>
    }

    <DeleteDialog
      isOpen={deletingFile != null}
      message={RESX.GeneralWarnings.AreYouSure}
      handleClose={() => { setDeletingFile(undefined); }}
      onDelete={onDeleted} />

    <Modal
      isOpen={isFromStorageOpen}
      title={RESX.File.Files}
      handleClose={() => { setIsFromStorageOpen(false); }}
      width="small"
      height={700}
    >
      <FromStorage
        onFilesSelected={onFilesFromStorageSelected}
        fetchRelatedFiles={fetchRelatedFilesApi}
        fileSizeLimit={props.fileGroup === "needToBeSigned"
          ? serverReference.constants.eSignFile.maxFileThatNeedsToBeSignedContentLength
          : serverReference.constants.eSignFile.maxOtherFileContentLength
        }
        validFileTypes={props.fileGroup === "needToBeSigned" ? "pdf" : "any"}
      />
    </Modal>

    <Modal
      isOpen={isFromTemplateOpen}
      title={RESX.WordTemplate.WordTemplates}
      handleClose={() => { setIsFromTemplateOpen(false); }}
      width="small"
      height={700}
    >
      <FromTemplate
        onTemplateSelected={onTemplateSelected}
        mainArea={props.documentDetails.documentType}
        bookkeeping={props.bookkeeping}
        documentId={props.documentDetails.documentId}
      />
    </Modal>
  </SectionPanel.Panel>;
};

export default FilesPanel;